import { useEffect, useState } from "react";
import { PhoneCall } from "lucide-react";
import { Label, LoadingState, RadioGroup, RadioGroupItem } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import {
  BUSY_MODES, announceBusyMode, getBusyMode, isBusyMode, setBusyMode, type BusyMode,
} from "../../../../lib/callBusyMode";
import { StatusPill } from "../../../ui";
import { IntegrationCard } from "../panel/IntegrationCard";
import { Note } from "../panel/fields";

/**
 * Settings -> Phone -> During a call: what a new call does while this person is
 * already on a call.
 *
 * The only tab in the Phone menu that belongs to a person rather than to the
 * line, which is why it answers to `use:crm.calls` (see `../perms`). The phone
 * app's Settings page writes the same value through the same route.
 *
 * A choice saves the moment it is made, like the Online switch: there is
 * nothing else on the card to save it together with. A failed save puts the
 * old choice back, so the card never shows a value the server does not hold.
 * A saved choice is announced to the other CRM tabs, so the call code in the
 * tab that holds the phone uses it at once.
 */
export function BusyModeCard() {
  const t = useT();
  const [mode, setMode] = useState<BusyMode | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  useEffect(() => {
    let live = true;
    getBusyMode()
      .then((res) => {
        if (live) setMode(res.busyMode);
      })
      .catch(() => {
        if (live) setLoadError(true);
      });
    return () => {
      live = false;
    };
  }, []);

  async function choose(value: string) {
    if (!isBusyMode(value) || value === mode || saveState === "saving") return;
    const before = mode;
    setMode(value);
    setSaveState("saving");
    try {
      const res = await setBusyMode(value);
      setMode(res.busyMode);
      setSaveState("saved");
      announceBusyMode(res.busyMode);
    } catch {
      setMode(before);
      setSaveState("error");
    }
  }

  const status =
    saveState === "saving" ? (
      <StatusPill tone="mut">{t("settings.busyMode.saving")}</StatusPill>
    ) : saveState === "saved" ? (
      <StatusPill tone="ok">{t("settings.busyMode.saved")}</StatusPill>
    ) : null;

  return (
    <IntegrationCard icon={PhoneCall} title={t("settings.busyMode.title")} desc={t("settings.busyMode.desc")} status={status}>
      {loadError ? (
        <Note tone="bad">{t("settings.busyMode.loadError")}</Note>
      ) : mode === null ? (
        <LoadingState variant="inline" label={t("common.loading")} />
      ) : (
        <div className="flex flex-col gap-3">
          <RadioGroup
            aria-label={t("settings.busyMode.label")}
            value={mode}
            onValueChange={(value) => void choose(value)}
            className="gap-2.5"
          >
            {BUSY_MODES.map((option) => (
              <Label
                key={option}
                htmlFor={`busy-mode-${option}`}
                className="flex cursor-pointer items-start gap-3 rounded-[20px] border border-[var(--crm-hair)] px-4 py-3 text-foreground has-[[data-state=checked]]:border-[var(--color-primary)]"
              >
                <RadioGroupItem value={option} id={`busy-mode-${option}`} className="mt-[3px]" />
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-[13px] font-[650]">{t(`settings.busyMode.${option}`)}</span>
                  <span className="crm-sub text-xs font-normal leading-snug">{t(`settings.busyMode.${option}Hint`)}</span>
                </span>
              </Label>
            ))}
          </RadioGroup>
          {saveState === "error" ? (
            <p className="text-sm text-destructive" role="alert">
              {t("settings.busyMode.saveError")}
            </p>
          ) : null}
        </div>
      )}
    </IntegrationCard>
  );
}
